import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert-dialog";
import { ExternalLink, KeyRoundIcon } from "lucide-react";

const STRONG_AUTH_DOCS_URL =
  "https://docs.arcade.dev/en/get-started/setup/strong-authentication";

export type StrongAuthRequiredDialogProps = {
  open: boolean;
  /** Opens Manage my account to set up strong authentication. */
  onManageAccount: () => void;
  onSignOut: () => void;
};

/**
 * Shown by the Dashboard when Arcade refuses the operator's sign-in because it
 * didn't use strong authentication. Nothing else in the Dashboard can load
 * until they fix that, so the dialog can't be dismissed.
 */
export function StrongAuthRequiredDialog({
  open,
  onManageAccount,
  onSignOut,
}: StrongAuthRequiredDialogProps) {
  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <KeyRoundIcon
              aria-hidden="true"
              className="size-5 shrink-0 text-muted-foreground"
            />
            Arcade requires secure sign-in
          </AlertDialogTitle>
          <AlertDialogDescription
            className="flex flex-col gap-2"
            render={<div />}
          >
            <p>
              Let's get you signed in securely. Arcade requires a passkey or
              two-factor authentication to continue.{" "}
              <a
                className="inline-flex items-center gap-1 underline hover:no-underline"
                href={STRONG_AUTH_DOCS_URL}
                rel="noopener"
                target="_blank"
              >
                Learn more
                <ExternalLink aria-hidden className="size-3.5" />
              </a>
            </p>
            <p>
              Head to Manage my account to check your sign-in methods and set
              one up if you need to.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onSignOut}>Sign out</AlertDialogCancel>
          <AlertDialogAction autoFocus onClick={onManageAccount} type="button">
            Manage my account
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
